import React, { useEffect, useState } from "react";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";

const money = (v) =>
  Number(v || 0).toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const campaignBonusLabel = (campaign) => {
  if (!campaign) return "";
  const fixed = Number(campaign.bonus_fixed_amount || 0);
  const percent = Number(campaign.bonus_percent || 0);
  if (fixed > 0) return `+€${money(fixed)} bonus fisso`;
  if (percent > 0) return `+${money(percent)}%`;
  return "Promo attiva";
};

const campaignDaysLabel = (campaign) => {
  const labels = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  const days = campaign?.days_of_week || [];
  if (!days.length) return "valida tutti i giorni";
  return "valida: " + days.map((d) => labels[Number(d)]).filter(Boolean).join(", ");
};

const campaignMinLabel = (campaign) => {
  const min = Number(campaign?.min_recharge_amount || 0);
  return min > 0 ? `ricarica minima €${money(min)}` : "nessuna ricarica minima";
};

const WalletClienteBox = ({ clientCode, refreshKey = 0 }) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CARTA");
  const [sendingRecharge, setSendingRecharge] = useState(false);

  const fetchWallet = async () => {
    if (!clientCode) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/clienti/${clientCode}/wallet/?_=${Date.now()}`);
      if (res.ok) {
        setWallet(await res.json());
      }
    } catch (e) {
      console.error("Errore fetch wallet:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    const amount = Number(rechargeAmount || 0);

    if (!amount || amount <= 0) {
      alert("Inserisci un importo valido per la ricarica.");
      return;
    }

    if (wallet?.active_campaign) {
      const c = wallet.active_campaign;
      const ok = window.confirm(
        `Promo attiva: "${c.name}"\n` +
        `${campaignBonusLabel(c)} - ${campaignMinLabel(c)} - ${campaignDaysLabel(c)}\n\n` +
        (c.gives_points
          ? `Questa ricarica entra nel credito normale e genera punti fedeltà secondo le regole standard.\n\n`
          : `Questa ricarica entra nel credito promo e non genera punti fedeltà sugli acquisti pagati con quel credito.\n\n`) +
        `Vuoi continuare con la ricarica?`
      );

      if (!ok) return;
    }

    setSendingRecharge(true);
    try {
      const res = await authFetch(`${API_URL}/api/clienti/${clientCode}/wallet/recharge/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, payment_method: paymentMethod, note: `Ricarica app da gestionale: €${amount.toFixed(2)}` }),
      });

      const json = await res.json();
      if (!res.ok) {
        alert(json.detail || "Errore ricarica wallet.");
        return;
      }

      setRechargeAmount("");
      await fetchWallet();
      alert(`Ricarica registrata. Importo €${money(json.amount)} - Bonus €${money(json.bonus_amount)} - Saldo €${money(json.total_balance)}`);
    } catch (e) {
      console.error("Errore ricarica wallet:", e);
      alert("Errore ricarica wallet.");
    } finally {
      setSendingRecharge(false);
    }
  };


  useEffect(() => {
    fetchWallet();

    const handleWalletRefresh = () => {
      fetchWallet();
    };

    window.addEventListener("dzr-wallet-refresh", handleWalletRefresh);

    return () => {
      window.removeEventListener("dzr-wallet-refresh", handleWalletRefresh);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCode, refreshKey]);

  if (loading) {
    return <div className="alert alert-light border">Caricamento credito app...</div>;
  }

  if (!wallet) return null;

  return (
    <div className="card shadow-sm border-0 mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <h4 className="mb-2">💰 Wallet cliente</h4>
          <span className={`badge ${wallet.enabled ? "bg-success" : "bg-secondary"}`}>
            {wallet.enabled ? "Wallet attivo" : "Wallet disattivo"}
          </span>
        </div>

        <div className="row g-3 mt-2">
          <div className="col-md-4">
            <div className="border rounded p-3 bg-light">
              <div className="small text-muted">Totale</div>
              <div className="fw-bold fs-4">€{money(wallet.total_balance)}</div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="border rounded p-3">
              <div className="small text-muted">Normale</div>
              <div className="fw-bold">€{money(wallet.normal_balance)}</div>
            </div>
          </div>

          <div className="col-md-4">
            <div className="border rounded p-3">
              <div className="small text-muted">Promo</div>
              <div className="fw-bold">€{money(wallet.promo_balance)}</div>
            </div>
          </div>
        </div>

        {wallet.active_campaign ? (
            <div className="alert alert-success mt-3">
              <div className="fw-bold">
                🎯 Promo wallet attiva: {wallet.active_campaign.name} ({campaignBonusLabel(wallet.active_campaign)})
              </div>
              <div className="small mt-1">
                {campaignMinLabel(wallet.active_campaign)} • {campaignDaysLabel(wallet.active_campaign)}
              </div>
              <div className="small mt-1">
                {wallet.active_campaign.gives_points
                  ? "Questa promo genera punti fedeltà: la ricarica viene caricata come credito normale."
                  : "Questa promo non genera punti fedeltà: la ricarica viene caricata come credito promo separato."}
              </div>
              {wallet.active_campaign.only_first_recharge && (
                <div className="small mt-1">
                  Valida solo sulla prima ricarica del cliente.
                </div>
              )}
            </div>
          ) : (
            <div className="alert alert-light border mt-3 mb-0">
              <div className="fw-bold">🎯 Nessuna promo wallet attiva</div>
              <div className="small text-muted">
                Le ricariche app vengono caricate come credito normale.
              </div>
            </div>
          )}


          <div className="border-top pt-3 mt-3">
          <div className="fw-bold mb-2">Ricarica App / Wallet</div>
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label small">Importo ricarica</label>
              <div className="input-group">
                <span className="input-group-text">€</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="form-control"
                  placeholder="Importo"
                  value={rechargeAmount}
                  onChange={(e) => setRechargeAmount(e.target.value)}
                />
              </div>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label small">Metodo pagamento</label>
              <select
                className="form-select"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <option value="CARTA">Carta</option>
                <option value="SATISPAY">Satispay</option>
              </select>
            </div>

            <div className="col-12 col-md-4">
              <button className="btn btn-success w-100" onClick={handleRecharge} disabled={sendingRecharge}>
                {sendingRecharge ? "Ricarico..." : "Ricarica App"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletClienteBox;
